import React, { useState } from 'react';
import { FormModal, Field, inputCls, btnGhost } from '../shared/FormKit';
import { inputXs, fieldCommentAddBtnActive } from './constants';

// The one "return for changes" dialog (docs/workflow-ui.md §4.3) — replaces
// the ~10 hand-built copies. Reason is always required (lifecycle rule:
// returning moves the request to awaiting_info and emails the reason to the
// requester). File/prop names keep the internal `reject` verb; the words the
// reviewer reads do not (see constants.js STATUS_META).
//
//   requestNo — used in the title: "Return <requestNo> for changes"
//   sideLabel — optional side being returned (Engagement Setup passes e.g. 'CaseWare')
//   fieldComments — the page's useFieldComments() handle, or null. Comments are
//                   WRITTEN next to the entries themselves (DetailItem); this
//                   dialog is where the reviewer sees the full set one last
//                   time before it goes out, because the mail and the requester
//                   get them all at once.
//   onSubmit  — async (reason, fieldComments[]) => void; errors are shown
//               inline, modal stays open
const RejectModal = ({ requestNo, sideLabel = null, fieldComments = null, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const comments = fieldComments ? fieldComments.list() : [];

  const submit = async () => {
    if (!reason.trim()) { setError('Please explain what needs to change.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(reason.trim(), comments);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <FormModal
      title={sideLabel ? `Return ${sideLabel} for Changes — ${requestNo}` : `Return ${requestNo} for Changes`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? 'Sending…' : 'Return for Changes'}
          </button>
        </>
      )}
    >
      <p className="text-sm text-slate-700">
        The request moves to <span className="font-medium">Changes required</span> and the requester
        receives your reason by email. They can update it and press{' '}
        <span className="font-medium">Re-submit</span> to re-enter the review queue.
      </p>

      {fieldComments && comments.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-semibold text-red-800 uppercase tracking-wide">
            Comments on {comments.length} entr{comments.length === 1 ? 'y' : 'ies'}
          </p>
          <div className="mt-2 space-y-2">
            {comments.map((c) => (
              <div key={c.field}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-red-800 uppercase">{c.label}</span>
                  <button
                    type="button"
                    onClick={() => fieldComments.remove(c.field)}
                    className={`${fieldCommentAddBtnActive} ml-auto`}
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  rows="2"
                  value={c.comment}
                  onChange={(e) => fieldComments.set(c.field, e.target.value)}
                  className={`${inputXs} bg-white mt-0.5`}
                />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-red-700 opacity-80 mt-2">
            They go out with this email and stay on the request. To comment on another entry,
            close this and use <span className="font-medium">Comment</span> next to it.
          </p>
        </div>
      )}

      <Field label="Reason" required>
        <textarea
          rows="3"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What needs to be corrected or added?"
          className={inputCls}
        />
      </Field>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
    </FormModal>
  );
};

export default RejectModal;
